import type { GInfoPanelProps } from './GInfoPanel';
import type { IconSource } from '@shopify/polaris';
import type { GPopoverProps } from '../GPopover';

import { GActivatorPopover } from '../GActivatorPopover';
import { useMemo, useCallback, useState } from 'react';
import { GClickable } from '../GClickable';
import { GInfoPanel } from './GInfoPanel';
import { Box } from '@shopify/polaris';
import { GPopover } from '../GPopover';

export interface GInfoPanelPopoverProps extends Omit<GInfoPanelProps, 'width'> {
  /** Text to display on the activator button */
  activatorText?: string;
  activatorIcon?: IconSource;
  /** Label to show when nothing is selected */
  emptyLabel?: string;
  searchEmpty?: string;
  /** Custom formatter for selected item label */
  formatActivatorContent?: (selectedLabel: string) => string;
  /** Whether to disable the popover */
  disabled?: boolean;
  /** Minimum width of the popover content */
  minWidth?: string;
  /** Maximum width of the popover content */
  maxWidth?: string;
  /** Minimum height of the popover content */
  minHeight?: string;
  /** Preferred alignment of the popover */
  preferredAlignment?: 'left' | 'right' | 'center';
  /** Preferred position of the popover */
  /** Whether to show full width popover */
  fullWidth?: boolean;
  /** Custom activator component (overrides default button) */
  activator?: React.ReactNode;
  popoverProps?: Pick<
    GPopoverProps,
    'fullWidth' | 'autofocusTarget' | 'preferredPosition' | 'maxWidth' | 'maxHeight'
  >;
  /** Callback when popover opens */
  onOpen?: () => void;
  /** Callback when popover closes */
  onClose?: () => void;
}

export const GInfoPanelPopover: React.FC<GInfoPanelPopoverProps> = ({
  activatorText,
  activatorIcon,
  emptyLabel,
  formatActivatorContent = (label: string) => label,
  disabled = false,
  minWidth = '600px',
  maxWidth = '800px',
  preferredAlignment = 'left',
  activator: customActivator,
  onOpen,
  onClose: onCloseCallback,
  items = [],
  groups = [],
  selectedId,
  onSelect,
  popoverProps,
  ...infoPanelProps
}) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState<string | undefined>(selectedId);

  const currentSelectedId = selectedId ?? internalSelectedId;

  // Combine all items from both items and groups to find selected item
  const allItems = useMemo(() => {
    const itemsList = [...items];
    groups.forEach((group) => {
      itemsList.push(...group.items);
    });
    return itemsList;
  }, [items, groups]);

  // Get selected item label
  const selectedItemLabel = useMemo(() => {
    if (!currentSelectedId) return null;
    const item = allItems.find((item) => item.id === currentSelectedId);
    if (!item) return null;
    return typeof item.label === 'string' ? item.label : null;
  }, [currentSelectedId, allItems]);

  // Format activator content
  const activatorContent = useMemo(() => {
    if (selectedItemLabel) {
      return `${activatorText ? `${activatorText}: ` : ''}${formatActivatorContent(selectedItemLabel)}`;
    }

    if (emptyLabel) {
      return `${activatorText}: ${emptyLabel}`;
    }

    return activatorText;
  }, [selectedItemLabel, emptyLabel, activatorText, formatActivatorContent]);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((prev) => {
      const nextState = !prev;
      if (nextState) {
        onOpen?.();
      } else {
        onCloseCallback?.();
      }
      return nextState;
    });
  }, [onOpen, onCloseCallback]);

  const handleSelect = useCallback(
    (id: string) => {
      setInternalSelectedId(id);
      onSelect?.(id);
      togglePopoverActive();
    },
    [onSelect, togglePopoverActive],
  );

  const defaultActivator = (
    <GActivatorPopover
      disabled={disabled}
      onClick={togglePopoverActive}
      active={popoverActive}
      textProps={{ tone: 'base' }}
      content={activatorContent ?? ''}
      icon={activatorIcon}
    />
  );

  return (
    <GPopover
      active={popoverActive}
      activator={
        customActivator ? (
          <GClickable onClick={togglePopoverActive}>{customActivator}</GClickable>
        ) : (
          defaultActivator
        )
      }
      onClose={togglePopoverActive}
      preferredAlignment={preferredAlignment}
      fixed={true}
      fluidContent
      {...popoverProps}
    >
      <Box width={minWidth} minWidth={minWidth} maxWidth={maxWidth} shadow="300">
        <GInfoPanel
          items={items}
          groups={groups}
          selectedId={currentSelectedId}
          onSelect={handleSelect}
          {...infoPanelProps}
        />
      </Box>
    </GPopover>
  );
};
